import React, { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { useFloating, autoUpdate, shift, offset } from "@floating-ui/react";
import { DayPicker } from "react-day-picker";
import { es } from "date-fns/locale";
import { format, subDays, startOfMonth, endOfMonth, subMonths, startOfYear, endOfYear, subYears } from "date-fns";
import CalendarIcon from "mdi-react/CalendarRangeIcon";
import CloseIcon from "mdi-react/CloseIcon";
import "react-day-picker/dist/style.css";

const formatDisplay = (d) => format(d, "dd-MM-yyyy");

const SHORTCUTS = [
  { key: "today", label: "Hoy", build: () => { const t = new Date(); return { from: t, to: t }; } },
  { key: "yesterday", label: "Ayer", build: () => { const y = subDays(new Date(), 1); return { from: y, to: y }; } },
  { key: "past7", label: "Últimos 7 días", build: () => ({ from: subDays(new Date(), 6), to: new Date() }) },
  { key: "past30", label: "Últimos 30 días", build: () => ({ from: subDays(new Date(), 29), to: new Date() }) },
  { key: "currentMonth", label: "Este mes", build: () => ({ from: startOfMonth(new Date()), to: endOfMonth(new Date()) }) },
  { key: "pastMonth", label: "Mes pasado", build: () => { const d = subMonths(new Date(), 1); return { from: startOfMonth(d), to: endOfMonth(d) }; } },
  { key: "currentYear", label: "Este año", build: () => ({ from: startOfYear(new Date()), to: endOfYear(new Date()) }) },
  { key: "pastYear", label: "Año pasado", build: () => { const d = subYears(new Date(), 1); return { from: startOfYear(d), to: endOfYear(d) }; } },
];

export default function DateRangePicker({ startDate, endDate, onChange, placeholder = "Seleccione el rango de fechas" }) {
  const [isOpen, setIsOpen] = useState(false);
  const [draft, setDraft] = useState({ from: undefined, to: undefined });

  const { refs, floatingStyles } = useFloating({
    open: isOpen,
    placement: "bottom-start",
    middleware: [offset(8), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate,
  });

  const handleClickOutside = useCallback((event) => {
    const inTrigger = refs.reference.current && refs.reference.current.contains(event.target);
    const inFloating = refs.floating.current && refs.floating.current.contains(event.target);
    if (!inTrigger && !inFloating) setIsOpen(false);
  }, [refs]);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen, handleClickOutside]);

  useEffect(() => {
    setDraft({
      from: startDate ? new Date(startDate) : undefined,
      to: endDate ? new Date(endDate) : undefined,
    });
  }, [startDate, endDate, isOpen]);

  const displayValue = startDate && endDate
    ? `${formatDisplay(new Date(startDate))} a ${formatDisplay(new Date(endDate))}`
    : "";

  const applyDraft = () => {
    const fromIso = draft.from ? format(draft.from, "yyyy-MM-dd") : null;
    const toIso = draft.to ? format(draft.to, "yyyy-MM-dd") : null;
    onChange(fromIso, toIso);
    setIsOpen(false);
  };

  const cancelDraft = () => {
    setDraft({
      from: startDate ? new Date(startDate) : undefined,
      to: endDate ? new Date(endDate) : undefined,
    });
    setIsOpen(false);
  };

  const clearAll = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onChange(null, null);
    setDraft({ from: undefined, to: undefined });
  };

  const applyShortcut = (sc) => {
    const next = sc.build();
    setDraft(next);
  };

  return (
    <>
      <style>{`
        .elp-rdp-root { --rdp-accent-color: #EAC024; --rdp-accent-background-color: #FEF3C7; --rdp-day_button-height: 36px; --rdp-day_button-width: 36px; font-size: 13px; margin: 0; }
        .elp-rdp-root .rdp-month_caption { font-weight: 600; color: #1F2937; padding: 8px 0; text-transform: capitalize; }
        .elp-rdp-root .rdp-weekday { color: #6B7280; font-weight: 500; font-size: 12px; }
        .elp-rdp-root .rdp-day { color: #374151; }
        .elp-rdp-root .rdp-day_button { border-radius: 8px; }
        .elp-rdp-root .rdp-day_button:hover { background: #F3F4F6; }
        .elp-rdp-root .rdp-selected .rdp-day_button { background: #EAC024 !important; color: white !important; }
        .elp-rdp-root .rdp-range_middle .rdp-day_button { background: #FEF3C7 !important; color: #B45309 !important; }
        .elp-rdp-root .rdp-range_start .rdp-day_button, .elp-rdp-root .rdp-range_end .rdp-day_button { background: #EAC024 !important; color: white !important; }
        .elp-rdp-root .rdp-today .rdp-day_button { color: #EAC024; font-weight: 700; }
        .elp-rdp-root .rdp-disabled .rdp-day_button { color: #D1D5DB; cursor: not-allowed; }
        .elp-rdp-root .rdp-chevron { fill: #6B7280; }
      `}</style>

      <div
        ref={refs.setReference}
        onClick={() => setIsOpen((o) => !o)}
        style={{
          flex: "1 1 280px",
          display: "flex",
          alignItems: "center",
          gap: 8,
          border: "1px solid #D1D5DB",
          borderRadius: 8,
          background: "white",
          padding: "8px 12px",
          cursor: "pointer",
          minHeight: 42,
          boxSizing: "border-box",
        }}
      >
        <CalendarIcon size={18} color="#6B7280" />
        <span style={{ flex: 1, fontSize: 14, color: displayValue ? "#374151" : "#9CA3AF" }}>
          {displayValue || placeholder}
        </span>
        {displayValue && (
          <button
            type="button"
            onClick={clearAll}
            title="Limpiar fechas"
            style={{ border: "none", background: "transparent", cursor: "pointer", color: "#9CA3AF", padding: 0, display: "flex" }}
          >
            <CloseIcon size={18} />
          </button>
        )}
      </div>

      {isOpen &&
        createPortal(
          <div
            ref={refs.setFloating}
            style={{
              ...floatingStyles,
              zIndex: 999999,
              background: "white",
              border: "1px solid #E5E7EB",
              borderRadius: 12,
              boxShadow: "0 10px 25px rgba(0,0,0,0.12)",
              overflow: "hidden",
              display: "flex",
              minWidth: 600,
            }}
          >
            <div style={{ background: "#F9FAFB", borderRight: "1px solid #E5E7EB", padding: 8, minWidth: 160, display: "flex", flexDirection: "column", gap: 2 }}>
              {SHORTCUTS.map((sc) => (
                <button
                  key={sc.key}
                  type="button"
                  onClick={() => applyShortcut(sc)}
                  style={{
                    textAlign: "left",
                    background: "transparent",
                    border: "none",
                    padding: "8px 12px",
                    borderRadius: 6,
                    fontSize: 13,
                    color: "#374151",
                    cursor: "pointer",
                    transition: "background 0.15s",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "#FEF3C7")}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                >
                  {sc.label}
                </button>
              ))}
            </div>

            <div style={{ display: "flex", flexDirection: "column" }}>
              <div style={{ padding: 12 }}>
                <DayPicker
                  className="elp-rdp-root"
                  mode="range"
                  numberOfMonths={2}
                  locale={es}
                  selected={draft}
                  onSelect={(range) => setDraft(range || { from: undefined, to: undefined })}
                  weekStartsOn={1}
                />
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, padding: "10px 12px", borderTop: "1px solid #E5E7EB", background: "#F9FAFB" }}>
                <button
                  type="button"
                  onClick={cancelDraft}
                  style={{ padding: "8px 16px", borderRadius: 6, border: "1px solid #D1D5DB", background: "white", color: "#374151", cursor: "pointer", fontSize: 13, fontWeight: 500 }}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={applyDraft}
                  style={{ padding: "8px 16px", borderRadius: 6, border: "none", background: "#EAC024", color: "white", cursor: "pointer", fontSize: 13, fontWeight: 600 }}
                >
                  Aplicar
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
}
